import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';

export const GroupTable = ({ group, teams, qualifyingThirdIds = [], anyMatchPlayed = true }) => {
  const isQualified = (team, index) => anyMatchPlayed && (index < 2 || qualifyingThirdIds.includes(team.id));
  const isThirdBest = (team, index) => anyMatchPlayed && index === 2 && qualifyingThirdIds.includes(team.id);
  return (
    <div className="glass-card overflow-hidden" data-testid={`group-table-${group.name}`}>
      <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-800 bg-slate-900/50">
        <h3 className="font-heading text-base sm:text-lg md:text-xl font-bold">
          GIRONE <span className="text-primary">{group.name}</span>
        </h3>
      </div>
      
      {/* Mobile View - Cards */}
      <div className="block sm:hidden p-3 space-y-3">
        {teams.map((team, index) => {
          const qualified = isQualified(team, index);
          const isThirdQ = isThirdBest(team, index);
          return (
          <div 
            key={team.id}
            className={cn(
              "p-3 rounded-lg border",
              qualified
                ? (isThirdQ ? "bg-gold/10 border-gold/30" : "bg-primary/10 border-primary/30")
                : "bg-slate-900/50 border-slate-800"
            )}
          >
            {/* Header: Position, Name, Points */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className={cn(
                  "font-numbers text-lg font-bold w-6 h-6 flex items-center justify-center rounded-full",
                  qualified ? (isThirdQ ? "bg-gold text-slate-950" : "bg-primary text-slate-950") : "bg-slate-800 text-slate-400"
                )}>{index + 1}</span>
                <span className="font-heading font-bold text-sm uppercase truncate">{team.name}</span>
                {isThirdQ && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gold/20 text-gold uppercase">3ª best</span>}
              </div>
              <div className="text-right flex-shrink-0">
                <span className="font-numbers text-2xl font-bold text-primary">{team.points}</span>
                <span className="text-[10px] text-slate-500 block">PT</span>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-7 gap-1 text-center">
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-500">G</span>
                <span className="font-numbers text-sm">{team.played}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-green-500">V</span>
                <span className="font-numbers text-sm text-green-500">{team.won}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-500">P</span>
                <span className="font-numbers text-sm">{team.drawn}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-red-500">S</span>
                <span className="font-numbers text-sm text-red-500">{team.lost}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-500">GF</span>
                <span className="font-numbers text-sm">{team.goals_for}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-500">GS</span>
                <span className="font-numbers text-sm">{team.goals_against}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-slate-500">DR</span>
                <span className={cn(
                  "font-numbers text-sm",
                  team.goal_difference > 0 ? 'text-green-500' : team.goal_difference < 0 ? 'text-red-500' : ''
                )}>
                  {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                </span>
              </div>
            </div>
          </div>
          );
        })}
      </div>
      
      {/* Desktop View - Table */}
      <div className="hidden sm:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="w-10 text-center text-slate-500 text-xs">#</TableHead>
              <TableHead className="text-slate-500 text-xs">Squadra</TableHead>
              <TableHead className="w-10 text-center text-slate-500 text-xs">G</TableHead>
              <TableHead className="w-10 text-center text-slate-500 text-xs">V</TableHead>
              <TableHead className="w-10 text-center text-slate-500 text-xs">P</TableHead>
              <TableHead className="w-10 text-center text-slate-500 text-xs">S</TableHead>
              <TableHead className="w-12 text-center text-slate-500 text-xs">GF</TableHead>
              <TableHead className="w-12 text-center text-slate-500 text-xs">GS</TableHead>
              <TableHead className="w-12 text-center text-slate-500 text-xs">DR</TableHead>
              <TableHead className="w-12 text-center font-bold text-primary text-xs">PT</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {teams.map((team, index) => {
              const qualified = isQualified(team, index);
              const isThirdQ = isThirdBest(team, index);
              return (
              <TableRow 
                key={team.id}
                className={cn(
                  "border-slate-800 transition-colors",
                  qualified ? (isThirdQ ? "bg-gold/5 border-l-2 border-l-gold" : "promotion-zone") : "hover:bg-slate-900/50"
                )}
                data-testid={`team-row-${team.id}`}
              >
                <TableCell className="text-center font-numbers text-base">
                  {index + 1}
                </TableCell>
                <TableCell className="font-heading font-semibold uppercase text-sm">
                  <span className="inline-flex items-center gap-2">
                    {team.name}
                    {isThirdQ && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gold/20 text-gold uppercase font-bold">3ª best</span>}
                  </span>
                </TableCell>
                <TableCell className="text-center font-numbers text-base">{team.played}</TableCell>
                <TableCell className="text-center font-numbers text-base text-green-500">{team.won}</TableCell>
                <TableCell className="text-center font-numbers text-base text-slate-400">{team.drawn}</TableCell>
                <TableCell className="text-center font-numbers text-base text-red-500">{team.lost}</TableCell>
                <TableCell className="text-center font-numbers text-base">{team.goals_for}</TableCell>
                <TableCell className="text-center font-numbers text-base">{team.goals_against}</TableCell>
                <TableCell className="text-center font-numbers text-base">
                  <span className={team.goal_difference > 0 ? 'text-green-500' : team.goal_difference < 0 ? 'text-red-500' : ''}>
                    {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                  </span>
                </TableCell>
                <TableCell className="text-center font-numbers text-xl font-bold text-primary">
                  {team.points}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      
      {teams.length > 0 && anyMatchPlayed && (
        <div className="px-3 sm:px-4 py-2 bg-slate-900/30 border-t border-slate-800 space-y-1">
          <p className="text-[10px] sm:text-xs text-slate-500">
            <span className="inline-block w-2 h-2 sm:w-3 sm:h-3 bg-primary/20 border-l-2 border-primary mr-1 sm:mr-2" />
            Qualificate ai quarti (1° e 2°)
          </p>
          {qualifyingThirdIds.length > 0 && (
            <p className="text-[10px] sm:text-xs text-slate-500">
              <span className="inline-block w-2 h-2 sm:w-3 sm:h-3 bg-gold/20 border-l-2 border-gold mr-1 sm:mr-2" />
              3ª miglior classificata (qualificata)
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupTable;
